// src/pages/admin/property/AdminEditProperty.tsx
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ImageUpload } from '@/components/admin/ImageUpload';
import { EditableFormField } from '@/components/admin/EditableFormField';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { usePropertyEditor } from '@/hooks/admin/usePropertyEditor';
import { BooleanToggleButton } from '@/components/admin/BooleanToggleField';
import ChevronLeftIcon from '@/image/common/ChevronLeftIcon';
// MultiDropdownField のインポート
import { MultiDropdownField } from '@/components/admin/Common/MultiDropdownField';
// useNeighborhoodList フックで API から areas を取得
import { useNeighborhoodList } from '@/components/admin/DropDowns';

export const AdminEditProperty: React.FC = () => {
  const { propertyId } = useParams<{ propertyId?: string }>();

  const {
    formData,
    images,
    loading,
    handleInputChange,
    handleAddImages,
    handleRemoveImage, // 画像削除用メソッド
    handleSaveAll,
  } = usePropertyEditor(propertyId);

  // API からエリア情報（areas）を取得
  const {
    areas,
    loading: areasLoading,
    error: areasError,
  } = useNeighborhoodList();

  if (loading || areasLoading) {
    return <div>Loading...</div>;
  }

  if (areasError) {
    return <div>エリア情報の取得に失敗しました。</div>;
  }

  if (!propertyId) {
    return <div>propertyId が指定されていません。</div>;
  }

  return (
    <AdminLayout>
      <div>
        {/* アイコン＋物件名 */}
        <div className="flex items-center pb-6 border-b border-gray-400">
          <span className="bg-gray-50 border border-black p-2 rounded">
            <Link to="/admin/property">
              <ChevronLeftIcon />
            </Link>
          </span>
          <span className="text-2xl ml-3">{formData.name}</span>
        </div>
        <div className="p-6 bg-yellow">
          {/* ステータス：BooleanToggleButton */}
          <BooleanToggleButton
            label="ステータス"
            value={formData.is_published}
            onToggle={(newValue) => {
              handleInputChange('is_published', newValue);
            }}
            height={33}
          />
          {/* 物件名 */}
          <EditableFormField
            label="物件名"
            field="name"
            subLabel1="(日本語表記)"
            defaultValue1={formData.name}
            subLabel2="(英語表記)"
            defaultValue2={formData.name_en}
            height={33}
            onValueChange={(values) => {
              handleInputChange('name', values.value1 || '');
              handleInputChange('name_en', values.value2 || '');
            }}
          />
          {/* 所在地 */}
          <EditableFormField
            label="所在地"
            field="address"
            subLabel1="(日本語表記)"
            defaultValue1={formData.address || ''}
            subLabel2="(英語表記)"
            defaultValue2={formData.address_en || ''}
            height={33}
            onValueChange={(values) => {
              handleInputChange('address', values.value1 || '');
              handleInputChange('address_en', values.value2 || '');
            }}
          />
          {/* 駅距離 */}
          <EditableFormField
            label="駅距離"
            field="station_distance"
            subLabel1="(日本語表記)"
            defaultValue1={formData.station_distance || ''}
            subLabel2="(英語表記)"
            defaultValue2={formData.station_distance_en || ''}
            height={33}
            onValueChange={(values) => {
              handleInputChange('station_distance', values.value1 || '');
              handleInputChange('station_distance_en', values.value2 || '');
            }}
          />
          {/* エリア部分をドロップダウンに変更 */}
          <MultiDropdownField
            label="エリア"
            count={1}
            values={[formData.area_id ? String(formData.area_id) : '']}
            options={areas.map((area) => ({
              id: area.id,
              name: area.name,
            }))}
            height={33}
            onChange={(newValues: string[]) => {
              handleInputChange('area_id', newValues[0] || '');
            }}
          />
          {/* 物件概要は元の EditableFormField のまま */}
          <EditableFormField
            label="物件概要"
            field="description"
            subLabel1="(日本語表記)"
            defaultValue1={formData.description || ''}
            subLabel2="(英語表記)"
            defaultValue2={formData.description_en || ''}
            height={138}
            onValueChange={(values) => {
              handleInputChange('description', values.value1 || '');
              handleInputChange('description_en', values.value2 || '');
            }}
          />
          {/* 写真登録 */}
          <ImageUpload
            label="写真登録"
            subLabel="(日本語表記)"
            images={images}
            onAddImages={handleAddImages}
            onRemoveImage={handleRemoveImage}
          />
          <button
            type="button"
            onClick={handleSaveAll}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
          >
            保存
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminEditProperty;
